import React, { useState, useEffect } from 'react'
import { router, usePage } from '@inertiajs/react';
import AppLayout from '../AppLayout';
import NotesGrid from '@/components/notes/NotesGrid';
import NotesList from '@/components/notes/list/NotesList';
import NotesTopBar from '@/components/notes/top-bar/NotesTopBar';
import { useNotesStore } from '@/store/notesStore';
import { useLoadNotes } from '@/api/notes/useLoadNotes';
import { useManageNote } from '@/api/notes/useManageNote';
import { Spinner } from '@/components/ui';
import NotesLoadError from '@/components/notes/NotesLoadError';
import NotesEmptySearch from '@/components/notes/NotesEmptySearch';
import { Head } from '@inertiajs/react';
import { DEFAULT_SORT, isValidSort } from '@/constants/notesSorting.constant';
import { apiPut } from '@/api/client';
import type { ViewMode } from '@/components/notes/top-bar/ViewModeToggle';
import { NotesListProvider } from '@/contexts/NotesListContext';
import { useNotesSearch } from '@/hooks/useNotesSearch';
import classNames from 'classnames/bind';
import animStyles from '@/components/notes/NoteAnimations.module.scss';
import { triggerErrorMessage } from '@/components/ui/toast';

const cx = classNames.bind(animStyles);

function getInitialSort(): string {
  const sort = new URLSearchParams(window.location.search).get('sort');
  return isValidSort(sort) ? sort : DEFAULT_SORT;
}

function getInitialTag(): string {
  return new URLSearchParams(window.location.search).get('tag') ?? '';
}

const App = () => {
  const { initialViewMode, encryptByDefault } = usePage<{ initialViewMode?: ViewMode; encryptByDefault?: boolean }>().props;

  const [sort, setSort] = useState<string>(getInitialSort);
  const [tagFilter, setTagFilter] = useState<string>(getInitialTag);
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode ?? 'grid');
  const { loading, error, refetch } = useLoadNotes(sort, tagFilter || undefined);
  const { notes, deleteNote } = useNotesStore();
  const { updateOrCreateNote } = useManageNote();
  const { query, setQuery, results } = useNotesSearch();
  const displayedNotes = results ?? notes;
  const showEmptySearch = query.trim() !== '' && results?.length === 0;

  const handleSortChange = (value: string) => {
    setSort(value);
    const url = new URL(window.location.href);
    url.searchParams.set('sort', value);
    window.history.pushState(window.history.state, '', url.toString());
  };

  const handleClearTagFilter = () => {
    setTagFilter('');
    const url = new URL(window.location.href);
    url.searchParams.delete('tag');
    window.history.pushState(window.history.state, '', url.toString());
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    apiPut('/app/v1/user/settings', { key: 'notes_view_mode', value: mode }).catch(
      (err) => console.error('Failed to persist view mode:', err),
    );
  };

  // Sync sort + tag from browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const s = params.get('sort');
      setSort(isValidSort(s) ? s : DEFAULT_SORT);
      setTagFilter(params.get('tag') ?? '');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // 8.4 — When a tag filter is active, remove notes that no longer match
  // after a tag removal (optimistic store update). The store already has
  // the updated tags array because useNoteTags applies optimistic updates,
  // so we just filter notes whose tags no longer intersect with the active filter.
  useEffect(() => {
    if (!tagFilter) return;
    const activeTags = tagFilter.split(',').map((t) => t.trim()).filter(Boolean);
    if (activeTags.length === 0) return;

    notes.forEach((note) => {
      const noteTags = note.tags ?? [];
      const stillMatches = activeTags.some((t) => noteTags.includes(t));
      if (!stillMatches) {
        deleteNote(note.id);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes.map((n) => (n.tags ?? []).join(',')).join('|')]);

  const handleCreateNote = async (type: 'MDX' | 'TXT' = 'MDX', selectedEncrypted?: boolean) => {
    try {
      const newNote = await updateOrCreateNote(null, {
        title: '',
        content: '',
        content_type: type,
        encrypted: selectedEncrypted ?? Boolean(encryptByDefault),
      });
      router.visit(`/app/note/${newNote.id}`);
    } catch (error) {
      console.error('Failed to create note:', error);
      if ((error as any)?.status === 409) {
        triggerErrorMessage('This tab has outdated account settings. Refresh it before creating a note.');
      } else {
        triggerErrorMessage((error as any)?.message || 'Failed to create note. Your text is still available to retry.');
      }
    }
  };

  const handleRetry = () => {
    refetch();
  };

  return (
    <>
      <Head>
        <title>My Notes</title>
      </Head>
      <NotesTopBar
        query={query}
        onQueryChange={setQuery}
        sort={sort}
        onSortChange={handleSortChange}
        onClickCreate={handleCreateNote}
        defaultEncrypted={Boolean(encryptByDefault)}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        activeTagFilter={tagFilter || undefined}
        onClearTagFilter={handleClearTagFilter}
      />
      {/* Spinner only on initial load (notes not yet fetched). Background
          refetches (e.g. after pin/unpin) keep the current list visible. */}
      {loading && notes.length === 0 && (
        <div className="flex items-center justify-center py-20">
          <Spinner size={50} enableTheme />
        </div>
      )}
      {!loading && error && notes.length === 0 && (
        <NotesLoadError error={error} onRetry={handleRetry} />
      )}
      <NotesListProvider refetch={refetch}>
        {/* Thin indeterminate bar — visible only during a background refetch
            (notes already loaded). Gives feedback on slow connections without
            hiding the list or showing a full spinner. */}
        <div className={cx('refetchBar', { refetchBarActive: loading && notes.length > 0 })} />
        {!error && showEmptySearch && <NotesEmptySearch />}
        {!error && displayedNotes.length > 0 && viewMode === 'list' && <NotesList notes={displayedNotes} searchQuery={query} />}
        {!error && displayedNotes.length > 0 && viewMode === 'grid' && <NotesGrid notes={displayedNotes} searchQuery={query} />}
      </NotesListProvider>
    </>
  );
}

App.layout = (page: React.ReactNode) => <AppLayout children={page} />;
export default App;
